import { BasePool } from 'core/components/reels/symbols/base/BasePool'
import { ReelSymbol } from 'core/components/reels/symbols/base/ReelSymbol'
import { gsap } from 'gsap'
import { signal } from 'services/SignalsService'
import { makeSymbolsService } from 'services/SymbolsService'
import { reelsSettings, spinSettings } from 'settings/custom/reels'
import { time } from 'settings/custom/rolling'
import { delay, delayCallBack } from 'utils/Delay'
import { log } from 'utils/Logger'
import { utils } from 'utils/Utils'

export default class CascadeReel extends PIXI.Container {
  constructor(data) {
    super()
    this.reelItems = []
    this.reelPosition = 0
    this.symbolPool = new BasePool()
    this.spinContainer = new PIXI.Container()
    this.overSymbolContainer = new PIXI.Container()
    this.id = parseFloat(data.name.match(/\d+/))
    this.itemCount = reelsSettings.itemCount[this.id]
    this.updateWin = false
    this.symbols = makeSymbolsService(this.id)
    if(game.models.CascadeModel.get().matrix.length === 0){
      this.matrix = game.models.SpinModel.get().matrix[this.id].slice()
    }
    else {
      this.matrix = game.models.CascadeModel.get().matrix.at(-1)[this.id].slice()
    }
    this.settings = spinSettings[game.models.ModeModel.turbo]
    this.addChild(this.spinContainer)
    this.overSymbolContainer = null
    this.createSignal()
  }

  setOverSymbolContainer(container){
    this.overSymbolContainer = container
  }

  init() {
    this.initSymbols()
  }

  initSymbols(){
    const itemHeight = game.models.ItemsModel.height
    for (let i = 0; i < this.itemCount; i++) {
      const item = new ReelSymbol(this.symbolPool)
      spinSettings.debug ? this.createTextDebug(item, i) : () => {}
      item.setSymbol(this.symbols.get())
      item.positionY = i
      item.y = (i) * itemHeight
      item.pos = i
      this.reelItems.push(item)
      this.spinContainer.addChild(item)
      game.models.SpinModel.setSymbol(this.id, i, item)
      item.resetSymbol()
    }

    this.spinContainer.y = -(this.itemCount) * itemHeight
    this.reelPosition = this.itemCount + 1
    this.updateSymbolPosition(true)
    this.resetSymbols()
    this.updateWin = true
    this.updateOnInitSymbols()
    this.canDrop = false

    if(reelsSettings.hasOwnProperty('overSymbols') && this.overSymbolContainer !== null){
      this.overSymbolContainer.y = -(this.itemCount) * itemHeight
    }

  }

  createSignal() {
    signal.once('STATE:OUT:INIT', this.init.bind(this))
    signal.on('END-STOP-REEL', (id) => {
      if(id === this.id) {
        this.spinEnd()
        if(reelsSettings.items.states['stop']){
          this.reelItems.forEach((el) => el.animationStop(el))
        }
        signal.dispatch(`SPIN-END-${id}`)
      }
    })

    signal.on('ACTION:REEL-SPIN', () => {
      this.canDrop = true
    })

  }

  getRandomInt(min, max) {
    min = Math.ceil(min)
    max = Math.floor(max)
    return Math.floor(Math.random() * (max - min) + min) // The maximum is exclusive and the minimum is inclusive
  }

  resetSymbols() {
    for (let i = 0; i < this.reelItems.length; i++) {
      this.reelItems[i].resetSymbol()
      if(spinSettings.debug){
        this.reelItems[i].debugText.text = i
      }
    }
  }

  resetSpin(){

  }

  updateOnInitSymbols(){
    let symbolCount = 0
    this.matrix.reverse()
    for (let i = 0; i <  this.itemCount; i++) {
      this.reelItems[i].visible = true
      const symbol = this.matrix.pop()
      this.reelItems[i].setSymbol(symbol)
      this._changeContainer(this.reelItems[i], symbol)
      game.models.SpinModel.setSymbol(this.id, symbolCount++, this.reelItems[i])
    }
  }

  updateSymbolWin(cascade, index){
    let symbolCount = 0

    if(cascade){
      this.matrix = game.models.CascadeModel.get().matrix[index][this.id].slice()
    }
    else{
      this.matrix = game.models.SpinModel.get().matrix[this.id].slice()
    }
    this.matrix.reverse()
    for (let i = 0; i <  this.itemCount; i++) {
      this.reelItems[i].visible = true
      const symbol = this.matrix.pop()
      this.reelItems[i].setSymbol(symbol)
      this._changeContainer(this.reelItems[i], symbol)
      game.models.SpinModel.setSymbol(this.id, symbolCount++, this.reelItems[i])
    }

  }

  _changeContainer(item, id){
    if(reelsSettings.hasOwnProperty('overSymbols')){
      const spinContainer = this.spinContainer
      const overContainer = this.overSymbolContainer
      const overSymbols = reelsSettings.overSymbols
      let isMatch = false
      overSymbols.symbol.forEach((symbol) => {
        if (symbol === id) {
          isMatch = true
        }
      })
      isMatch ? utils.changeParent(item, overContainer) : utils.changeParent(item, spinContainer)
    }
  }

  updateSymbolPosition(){
    const itemHeight = game.models.ItemsModel.height
    const reelItem =  this.reelItems.length
    for (let j = 0; j < reelItem; j++) {
      const s = this.reelItems[j]
      s.y = (this.reelPosition + j) * itemHeight - itemHeight
    }
  }

  cascadeMoveToTop(){
    this.reelItems.forEach((s) => {
      s.y = spinSettings.cascadePositionTop
    })
    if(this.canDrop){
      this.cascadeDropIn()
    }
    else{
      delay(spinSettings.cascadeDelayTopCanDrop).then(() => {
        this.cascadeMoveToTop()
      })
    }
  }

  cascadeDropIn(){
    const tSettings = spinSettings.cascadeDropInTweenSettings
    const itemHeight = game.models.ItemsModel.height
    const reelItem =  this.reelItems.length - 1
    this.resetSymbols()
    this.updateSymbolWin()
    for (let j = reelItem; j >= 0; j--) {
      const s = this.reelItems[j]
      const tweenSort = gsap.to(s, {
        y: (this.reelPosition + j) * itemHeight - itemHeight,
        ease: tSettings.ease,
        duration: tSettings.duration,
        delay: tSettings.delay * (reelItem - j),
        onComplete: () => {
          if(j === 0){
            tweenSort.kill()
            this.canDrop  = false
            signal.dispatch('END-STOP-REEL', this.id)
          }
        },
      })
    }
  }

  removeElementsByIndex(arr, indicesToRemove) {
    const removedElements = []
    // Sort indices in descending order to prevent issues when removing elements
    indicesToRemove.sort((a, b) => b - a)
    indicesToRemove.forEach((index) => {
      if (index >= 0 && index < arr.length) {
        removedElements.push(arr.splice(index, 1)[0]) // Remove the element at the specified index and add it to the removedElements array
      }
    })
    return removedElements
  }

  checkIDPositionCascade(positions) {
    const sortArr = []
    if(positions !== undefined) {
      positions.forEach((pos) => {
        if(pos[0] === this.id){
          sortArr.push(pos[1])
        }
      })
    }

    return sortArr

  }

  showCascadeWin(sortedIndex) {
    if(sortedIndex.length > 0){
      this.cascadeDisappears(sortedIndex)
    }
    else{
      setTimeout(() => {
        signal.dispatch(`SHOW-WIN-END-${this.id}`)
      }, 1)
    }

    return signal.promise( `SHOW-WIN-END-${this.id}`)
  }

  showCascadeDrop(sortedIndex, explosionIndex) {
    if(sortedIndex.length > 0){
      this.updateSymbolWin(true, explosionIndex)
      this.sortCascade()
    }
    else{
      setTimeout(() => {
        signal.dispatch(`SHOW-DROP-END-${this.id}`)
      }, 1)
    }

    return signal.promise( `SHOW-DROP-END-${this.id}`)
  }

  cascadeDisappears(sortedIndex) {
    sortedIndex.forEach((index) => {
      this.reelItems[index].playCascadeWin()
      delay(time.cascadeWin).then(() => {
        this.reelItems[index].playCascadeVanish()
      })
    })

    delay(time.cascadeWin + time.cascadeDisappear).then(() => {
      sortedIndex.forEach((index) => {
        this.reelItems[index].y = 0
      })

      const removed =  this.removeElementsByIndex(this.reelItems, sortedIndex)
      removed.forEach((r) => {
        this.reelItems.unshift(r)
      })
      signal.dispatch(`SHOW-WIN-END-${this.id}`)
    })
  }

  sortCascade(){
    const itemHeight = game.models.ItemsModel.height
    const reelItem =  this.reelItems.length - 1
    const tSettings = spinSettings.cascadeSortTweenSettings
    const time = reelsSettings.time ? reelsSettings.time : 1
    for (let j = reelItem; j >= 0; j--) {
      const s = this.reelItems[j]
      const tweenSort = gsap.to(s, {
        y: (this.reelPosition + j) * itemHeight - itemHeight,
        ease: tSettings.ease,
        duration: tSettings.duration,
        delay: tSettings.delay * (reelItem - j),
        onComplete: () => {
          s.playCascadeDrop()
          if(j === 0) {
            tweenSort.kill()
            delayCallBack(time, () => {
              signal.dispatch(`SHOW-DROP-END-${this.id}`)
            }).then(() => log('CASCADE-SORT-COMPLITED'))
          }
        },
      })
    }

  }

  cascadeDropOut(){
    log('CASCADE-DROP')
    const tSettings = spinSettings.cascadeDropOutSettings
    const reelItem =  this.reelItems.length - 1
    for (let j = 0; j <= reelItem; j++) {
      const s = this.reelItems[reelItem - j]
      const tweenDrop = gsap.to(s, {
        y: tSettings.y,
        ease: tSettings.ease,
        duration: tSettings.duration,
        delay: tSettings.delay * j,
        onComplete: () => {
          if(j === reelItem){
            this.cascadeMoveToTop()
            tweenDrop.kill()
            log('CASCADE-DROP-COMPLITED')
          }
        },
      })
    }
  }

  async spin() {
    this.resetSpin()
    this.updateSymbolPosition()
    this.cascadeDropOut()
    return signal.promise( `SPIN-END-${this.id}`)
  }

  spinEnd(){
    this.resetSymbols()
  }

  async stop() {

  }

  move(v) {
    this.reelItems.forEach((el) => el.setType(v ? 'move' : false))
  }

  refresh(isFs) {
    this.matrix.push(...game.models.SpinModel.get().matrix[this.id].slice())
    this.reelItems.forEach((el, pos) => {
      el.pos = pos
      el.update(isFs)
    })

  }

  createTextDebug(item, i){
    const style = new PIXI.TextStyle({
      fill: 'gray',
      fontSize: 35,
      stroke: 'white',
      strokeThickness: 2,
    })
    const text = new PIXI.Text(`${i}`, style)
    text.x = 50
    text.y = 50
    item.addChild(text)
    item.debugText = text
  }
}
